<template>
    <AppHead title="Subscription | 10xTravel Plus" />
    <div class="px-4 md:px-8 py-2 md:py-4 relative">
        <div
            class="flex flex-col md:flex-row md:justify-between items-start md:items-center mb-5"
        >
            <div>
                <h2 class="text-2xl font-semibold text-slate-800 dark:text-slate-100">
                    10xTravel Plus
                </h2>
                <p class="text-slate-500 dark:text-slate-400 text-sm">
                    Access your exclusive travel benefits and features
                </p>
            </div>
        </div>

        <div class="flex pb-8 flex-col items-start gap-1 self-stretch">
            <div class="flex items-center gap-2">
                <h3 class="font-medium text-lg text-slate-900 dark:text-slate-100">Subscription</h3>
            </div>
            <p class="text-md text-slate-500 dark:text-slate-400">
                Manage your membership subscription.
            </p>
        </div>

        <div
            class="flex p-4 md:p-6 flex-col items-start self-stretch rounded-lg bg-white dark:bg-slate-800 shadow-md"
        >
            <!-- Shared wallet access (no personal subscription) -->
            <template
                v-if="
                    subscription?.shared_wallet_access &&
                    !subscription?.has_subscription
                "
            >
                <div class="flex flex-col items-start gap-1 mb-5">
                    <div class="flex flex-wrap items-center gap-1">
                        <h2 class="font-medium text-lg text-slate-900 dark:text-slate-100">
                            10xTravel+ membership
                        </h2>
                        <div
                            class="flex px-2 py-1 items-center gap-1 rounded-full bg-green-200"
                        >
                            <Icon icon="CheckIcon" class="w-4 h-4" />
                            <span class="text-sm text-green-700">Active</span>
                        </div>
                    </div>
                    <div class="text-sm text-slate-500 dark:text-slate-400">
                        Access provided through your shared wallet
                    </div>
                </div>
            </template>

            <!-- Personal subscription -->
            <template v-else>
                <div class="flex flex-col items-start gap-1 mb-5">
                    <div class="flex flex-wrap items-center gap-1">
                        <h2 class="font-medium text-lg text-slate-900 dark:text-slate-100">
                            10xTravel+ membership
                        </h2>
                        <div
                            class="flex px-2 py-1 items-center gap-1 rounded-full bg-green-200"
                            v-if="
                                !subscription?.on_grace_period &&
                                subscription?.stripe_status === 'active'
                            "
                        >
                            <Icon icon="CheckIcon" class="w-4 h-4" />
                            <span class="text-sm text-green-700">Active</span>
                        </div>
                        <div
                            class="flex px-2 py-1 items-center gap-1 rounded-full bg-amber-200"
                            v-if="
                                subscription?.on_grace_period &&
                                subscription?.stripe_status === 'active'
                            "
                        >
                            <span class="text-sm text-amber-700">Expires</span>
                        </div>
                        <div
                            class="flex px-2 py-1 items-center gap-1 rounded-full bg-red-200"
                            v-if="
                                subscription?.stripe_status !== 'active' &&
                                !subscription?.shared_wallet_access
                            "
                        >
                            <span class="text-sm text-red-700">Expired</span>
                        </div>
                    </div>
                    <div
                        class="flex flex-wrap items-center gap-1 text-sm text-slate-500 dark:text-slate-400"
                    >
                        <span
                            v-if="
                                !subscription?.on_grace_period &&
                                subscription?.stripe_status === 'active'
                            "
                        >
                            Renewal date {{ subscriptionEnds }}
                        </span>
                        <span
                            v-if="
                                subscription?.on_grace_period &&
                                subscription?.stripe_status === 'active'
                            "
                        >
                            Expires on {{ subscriptionEnds }}
                        </span>
                        <span>&#8226;</span>
                        <span>Annual payment, $199</span>
                    </div>
                </div>

                <button
                    class="btn btn-secondary"
                    type="button"
                    @click="cancelSubscription()"
                    v-if="
                        subscription?.stripe_status === 'active' &&
                        !subscription?.on_grace_period
                    "
                >
                    Cancel Subscription
                </button>
                <button
                    class="btn btn-orange !ml-0"
                    type="button"
                    @click="renewSubscription()"
                    v-if="
                        subscription?.stripe_status === 'active' &&
                        subscription?.on_grace_period
                    "
                >
                    Renew Subscription
                </button>

                <hr class="border-t border-slate-200 dark:border-slate-700 w-full my-10" />

                <h4 class="text-lg font-medium text-slate-800 dark:text-slate-100 mb-8">
                    Payments
                </h4>

                <ul class="text-sm w-full">
                    <li
                        class="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 mb-4 md:mb-2 pb-4 md:pb-0 border-b md:border-b-0 border-slate-100 last:border-b-0"
                        v-for="payment in subscriptionPayments"
                        :key="payment.id"
                    >
                        <span class="text-slate-700 dark:text-slate-200">
                            {{
                                dayjs(payment.created * 1000).format(
                                    'MMM D, YYYY'
                                )
                            }}
                            - {{ payment.number }} -
                            {{
                                currency(
                                    (payment.total / 100).toFixed(2)
                                ).format()
                            }}
                        </span>
                        <a
                            class="btn btn-secondary btn-sm w-full md:w-auto text-center"
                            :href="`/api/travel-plus/user/invoice/${payment.id}`"
                        >
                            Download invoice
                        </a>
                    </li>
                </ul>
            </template>
        </div>

        <div class="mt-6">
            <Link
                href="/10xtravel-plus"
                class="text-orange hover:text-orange-600 font-medium"
            >
                &larr; Back to 10xTravel Plus
            </Link>
        </div>
    </div>
</template>

<script setup>
import { onMounted } from 'vue';
import { Link } from '@inertiajs/vue3';
import { storeToRefs } from 'pinia';
import { useTravelPlusStore } from '@/store/travelplus';
import AppHead from '@/Partials/AppHead.vue';
import Icon from '@/components/ui/icon/icon.vue';
import dayjs from 'dayjs';
import currency from 'currency.js';

const travelPlusStore = useTravelPlusStore();
const {
    loadingSubscription,
    loadingSubscriptionPayments,
    subscription,
    subscriptionPayments,
    subscriptionEnds,
} = storeToRefs(travelPlusStore);

const cancelSubscription = () => {
    travelPlusStore.cancelSubscription();
};

const renewSubscription = () => {
    travelPlusStore.renewSubscription();
};

onMounted(() => {
    travelPlusStore.getSubscription();
    travelPlusStore.getSubscriptionPayments();
});
</script>
